import { path, propEq, pipe, complement, anyPass, isEmpty, isNil, find, includes } from 'ramda'
import { useEffect } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import styled from 'styled-components'
import { useTranslation, Trans } from 'react-i18next'
import PageTitle from '@/components/page-title'
import { select } from '@/store'
import { breakpoints, colors } from '@/styles'
import { Button } from '@/components/button'
import { Box } from '@/components/structure'
import StatusBadge from '@/components/status-badge'
import { Label, H5 } from '@/components/text'
import { show as showReviewTermsModal } from './review-terms-modal'
import FolderIcon from './folder-icon.svg?react'
import LivContractIcon from './liv-contract-icon.svg?react'
import CustomContractIcon from './custom-contract-icon.svg?react'

const Container = styled.div`
  max-width: 1192px;
  margin: 28px;
`

const GridContainer = styled.div`
  display: grid;
  grid-template-columns: 48px 3fr;
  width: 100%;
  gap: 16px;

  @media screen and (max-width: ${breakpoints.ipadMiniMax}) {
    grid-template-columns: 1fr;
  }
`

const MainContent = styled.div``

const Address = styled.h5`
  font-size: 20px;
  font-weight: 700;
  margin: 10px 0;
`

const SelectionWrapper = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  max-width: 904px;
  margin-top: 40px;
  gap: 28px;
`

const CardsWrapper = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 16px;

  @media screen and (max-width: ${breakpoints.tabletLandscape}) {
    grid-template-columns: 1fr;
  }
`

const Card = styled.div`
  display: flex;
  flex-direction: column;
  background-color: ${colors.white};
  text-align: left;
  border-radius: 16px;
  padding: 16px;
`

const ContractHero = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  height: 164px;
  border-radius: 8px;
  background-color: ${props => props.color};
`

const HeroDescription = styled(Label)`
  color: ${colors.grey48};
  line-height: 24px;
  margin: 8px 0 24px;
`

const ButtonWrapper = styled.div`
  margin-top: auto;
  > a,
  button {
    width: 100%;
  }
`

const FolderIconImage = styled(FolderIcon)`
  margin-left: -4px;

  @media screen and (max-width: ${breakpoints.ipadMiniMax}) {
    display: none;
  }
`

const EmailLink = styled.a`
  color: ${colors.link};
`

const isDraft = propEq('state_machine', 'draft')

const ALLOWED_PROVINCES = ['BC', 'ON', 'AB']

const ContractTypes = ({
  match: {
    params: { listingId }
  },
  history,
  location
}) => {
  const dispatch = useDispatch()
  const { t } = useTranslation()
  const contract = useSelector(path(['listing', listingId, 'contract']))
  const listing = useSelector(path(['listing', listingId]))
  const contractId = contract?.listing_contract_id ?? ''
  const buildingState = listing?.building?.state
  const listingInSupportedState = includes(buildingState, ALLOWED_PROVINCES)
  const typeOfContract = contract?.contract_structure
  const hasAgreedToCustomContractTerms = contract?.contract_terms_accepted
  const noContract = !contract || !contractId
  const address = contract?.address || listing?.building?.full_address

  const { packageFiles = [] } = useSelector(state =>
    select.contract.contractPackageInfo(state, { contractId: contractId })
  )

  const hasTenancyPackageFile = pipe(
    find(propEq('document_type', 'tenancy_agreement')),
    complement(anyPass([isEmpty, isNil]))
  )(packageFiles)

  useEffect(() => {
    dispatch.listing.load(listingId)
  }, [dispatch.listing, listingId])

  useEffect(() => {
    dispatch.listing.getContract(listingId)
  }, [dispatch.listing, listingId])

  useEffect(() => {
    async function fetchData() {
      await dispatch.contract.getPackage({ contractId })
    }

    if (contractId) fetchData()
  }, [contractId, dispatch.contract])

  const getContractLink = (state = {}) => {
    if (state?.applicationId) return `/application/${state?.applicationId}/contract/create`
    if (noContract || isDraft(contract)) {
      return `/listing/${listingId}/contract/create/${contractId || ''}`
    }
    return `/listing/${listingId}/contract/${contractId}`
  }

  const handleMoveForward = () => {
    showReviewTermsModal({
      listingId,
      contractId: contractId,
      history: history
    })
  }

  const changeToCustomContract = async () => {
    const { response } = await dispatch.contract.changeContractType({
      contractId: contractId,
      contractStructure: 'custom'
    })
    if (response.ok) {
      const route = `/custom-contract/create/${contractId}`
      history.replace(route)
    }
  }

  const getNativeLink = () =>
    history.replace({ pathname: getContractLink(location.state), state: location.state })

  const changeToNativeContract = async () => {
    const { response } = await dispatch.contract.changeContractType({
      contractId: contractId,
      contractStructure: 'native'
    })
    if (response.ok) {
      getNativeLink()
    }
  }

  const handleCustomContract = hasAgreedToCustomContractTerms
    ? changeToCustomContract
    : handleMoveForward

  const handleNativeContract = noContract ? getNativeLink : changeToNativeContract

  return (
    <Container>
      <PageTitle>{address}</PageTitle>
      <GridContainer>
        <FolderIconImage />
        <MainContent>
          <SelectionWrapper>
            <H5 as="h2">
              {t(
                'l.application.view.how_would_you_like_to_create.button',
                'How would you like to create your lease?'
              )}
            </H5>
            <CardsWrapper>
              <Card>
                <ContractHero color={colors.red96}>
                  <LivContractIcon />
                </ContractHero>
                <Box top={16} bottom={16}>
                  {listingInSupportedState ? (
                    <StatusBadge
                      title={t('l.contract.types.recommended.button', 'Recommended')}
                      type="success"
                    />
                  ) : (
                    <StatusBadge
                      title={t('l.contract.types.not_supported.button', 'Not Supported')}
                      type="default"
                    />
                  )}
                </Box>
                <Label fontSize="18px">{`${buildingState} Tenancy Agreement`}</Label>
                <HeroDescription type="small">
                  {listingInSupportedState ? (
                    t(
                      'l.contract.types.bc_description.button',
                      'The easiest way to create & sign your lease! Our Residential Tenancy Agreements are always matched to the correct province and regulations.'
                    )
                  ) : (
                    <Trans
                      i18nKey="l.contract.types.not_supported_description.text"
                      values={{ buildingState }}
                    >
                      We currently do not support tenancy agreements in {{ buildingState }}. If
                      you’d like us to support your province’s tenancy agreement, please send a
                      request at{' '}
                      <EmailLink href="mailto:support@liv.rent">support@liv.rent</EmailLink>.
                    </Trans>
                  )}
                </HeroDescription>
                <ButtonWrapper>
                  <Button
                    theme="primary"
                    disabled={!listingInSupportedState}
                    onClick={handleNativeContract}
                  >
                    {typeOfContract && typeOfContract === 'native' ? 'Continue' : 'Start Here'}
                  </Button>
                </ButtonWrapper>
              </Card>
              <Card>
                <ContractHero color={colors.tagsPurple}>
                  <CustomContractIcon />
                </ContractHero>
                <Box top={16} bottom={16}>
                  <StatusBadge title="Beta" type="blue" />
                </Box>
                <Label fontSize="18px">liv.sign Contract Builder&reg;</Label>
                <HeroDescription type="small">
                  Using our unique tool, you will be uploading your own tenancy agreement and any
                  related documents to have them reviewed and signed by all parties.
                </HeroDescription>
                <ButtonWrapper>
                  <Button theme="primary" onClick={handleCustomContract}>
                    {typeOfContract && typeOfContract === 'custom' ? 'Continue' : 'Try Now'}
                  </Button>
                </ButtonWrapper>
              </Card>
            </CardsWrapper>
          </SelectionWrapper>
        </MainContent>
      </GridContainer>
    </Container>
  )
}

export default ContractTypes
